<template>
  <div class="home-page">
    <BannerCarousel :slides="bannerSlides" :interval="3000" />
    
    <section class="products-section section">
      <h2 class="section-title products-slogan">软件定义基础设施，AI创造无限未来</h2>
      
      <div class="products-tabs">
        <button 
          v-for="tab in productTabs"
          :key="tab.key"
          :class="['tab-btn', { active: activeTab === tab.key }]"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>
      
      <div class="products-grid" :class="{ 'products-grid--token': isTokenGatewayTab }">
        <div
          v-if="isTokenGatewayTab"
          class="token-gateway-stage"
        >
          <div
            class="token-gateway-bg"
            aria-hidden="true"
            :style="{ backgroundImage: `url(${tokenGatewayBg})` }"
          ></div>
          <router-link
            class="token-bg-hit token-bg-hit--start"
            to="/product/ai-token-gateway"
            aria-label="开始使用"
          />
          <router-link
            class="token-bg-hit token-bg-hit--detail"
            to="/product/ai-token-gateway"
            aria-label="了解详情"
          />
        </div>
        <router-link
          v-for="product in currentProducts"
          :key="product.title"
          :to="product.path"
          class="product-card"
        >
          <div
            v-if="product.bgImage"
            class="product-bg-image"
            :style="{
              backgroundImage: `url(${product.bgImage})`,
              backgroundSize: product.bgSize || 'auto 100%',
              backgroundPosition: product.bgPosition || 'right center',
              transform: product.bgScale ? `scale(${product.bgScale})` : undefined,
              transformOrigin: product.bgScale ? 'right center' : undefined
            }"
          ></div>
          <div class="product-content">
            <div class="product-title-block">
              <span class="product-title-accent" aria-hidden="true"></span>
              <h3 class="product-title">{{ product.title }}</h3>
            </div>
            <div class="product-title-divider"></div>
            <p class="product-desc">{{ product.desc }}</p>
            <span v-if="!isTokenGatewayTab" class="product-btn">了解详情 →</span>
          </div>
        </router-link>
        <aside v-if="isTokenGatewayTab" class="token-gateway-intro">
          <h3 class="token-intro-title">
            AI Token网关，<br>
            <em>海量AI模型</em>
          </h3>
          <p class="token-intro-desc">
            统一接入、智能调度、安全可控的多模型 AI 入口
          </p>
          <div class="token-intro-actions">
            <router-link class="token-intro-btn token-intro-btn--primary" to="/product/ai-token-gateway">
              开始使用
              <span aria-hidden="true">→</span>
            </router-link>
            <router-link class="token-intro-btn token-intro-btn--ghost" to="/product/ai-token-gateway">
              了解详情
            </router-link>
          </div>
        </aside>
      </div>
    </section>
    
      <Solutions />
      
      <section id="success-cases" class="stats-section section" ref="statsSectionRef">
        <h2 class="section-title">成功案例</h2>
        <div class="stats-grid">
          <div v-for="(stat, i) in statsItems" :key="stat.label" class="stat-item">
            <div class="stat-value">{{ displayStatsValue(i) }}<span class="stat-plus">+</span></div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
        
        <div class="testimonials-section">
          <div
            class="testimonial-carousel"
            @mouseenter="pauseTestimonialAutoplay"
            @mouseleave="resumeTestimonialAutoplay"
          >
            <button class="carousel-arrow prev" @click="prevTestimonial">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15,18 9,12 15,6"></polyline>
              </svg>
            </button>
            
            <div class="testimonial-card-wrapper">
              <div
                class="testimonial-large-card"
              >
                <div
                  class="testimonial-card-content"
                  :class="{ 'is-tripartite': currentTestimonial.tripartite }"
                >
                  <div class="testimonial-left">
                    <div class="testimonial-main-text">
                      <p
                        v-for="(para, i) in testimonialParagraphs"
                        :key="i"
                      >{{ para }}</p>
                    </div>
                    <div class="testimonial-footer">
                      <div class="testimonial-author-info">
                        <span class="author-name">{{ currentTestimonial.author }}</span>
                        <span class="testimonial-tag-line">{{ currentTestimonial.tag }}</span>
                      </div>
                    </div>
                  </div>
                  <template v-if="currentTestimonial.tripartite">
                    <div class="testimonial-diagram">
                      <img
                        class="testimonial-diagram-art"
                        :src="currentTestimonial.diagramImage"
                        alt=""
                      />
                    </div>
                    <div class="testimonial-feature">
                      <img
                        class="testimonial-feature-art"
                        :src="currentTestimonial.featureImage"
                        alt=""
                      />
                    </div>
                  </template>
                  <div v-else class="testimonial-visual">
                    <img
                      v-if="currentTestimonial.bgImage"
                      class="testimonial-visual-art"
                      :src="currentTestimonial.bgImage"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <button class="carousel-arrow next" @click="onNextTestimonialClick">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </button>
            
            <div class="carousel-dots">
              <span 
                v-for="(item, index) in testimonials" 
                :key="index" 
                :class="['dot', { active: activeTestimonialIndex === index }]"
                @click="goToTestimonial(index)"
              ></span>
            </div>
          </div>
        </div>
      </section>
    
    <Partners />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import BannerCarousel from '@/components/common/BannerCarousel.vue'
import Solutions from '@/components/Solutions.vue'
import Partners from '@/components/Partners.vue'

const bannerSlides = [
  {
    image: new URL('@/assets/banner7.png', import.meta.url).href,
    title: '以安全资源池为底座|智能编排调度为基石  构建全域智能云安全体系',
    darkText: true,
    label: '全域智能云安全体系',
    cropTop: true
  },
  {
    image: new URL('@/assets/banner8.png', import.meta.url).href,
    title: '软件定义基础设施|AI赋能智算  驱动全域智能的新一代信息基础设施',
    lightText: true,
    label: 'AI赋能新一代信息基础设施',
    cropY: true
  }
]

const activeTab = ref('token-gateway')

const productTabs = [
  { key: 'token-gateway', label: 'AI Token 网关' },
  { key: 'cloud', label: 'AI智算产品' },
  { key: 'security', label: '云安全' },
  { key: 'edge-cloud', label: '云基础设施' },
  { key: 'compute-network', label: '算力网络' },
  { key: 'cloud-security', label: '安全产品' }
]

const products = {
  'security': [
    { title: '云安全资源池', desc: '安全能力池化，为租户/业务按需提供防护能力', icon: new URL('@/assets/products/PIcon/p1.png', import.meta.url).href, path: '/product/cloud-security-resource-pool', bgImage: new URL('@/assets/products/bg/cloud-security-resource-pool.png', import.meta.url).href },
    { title: '流量编排器', desc: '智能流量编排，业务路径重塑', icon: new URL('@/assets/products/PIcon/p3.png', import.meta.url).href, path: '/product/sslo', bgImage: new URL('@/assets/products/bg/sslo.png', import.meta.url).href },
    { title: '等保一体机', desc: '集等保二级、三级安全能力一体，满足快速合规要求', icon: new URL('@/assets/products/PIcon/p7.png', import.meta.url).href, path: '/product/equal-protection-appliance', bgImage: new URL('@/assets/products/bg/equal-protection-appliance.png', import.meta.url).href }
  ],
  'edge-cloud': [
    { title: '超融合', desc: '存算一体，提供计算、存储、网络虚拟化', icon: new URL('@/assets/products/PIcon/p4.png', import.meta.url).href, path: '/product/edge-cloud/containers', bgImage: new URL('@/assets/products/bg/hyperconverged.svg', import.meta.url).href },
    { title: '边缘云', desc: '小型边缘节点，可提供多租户、多VPC云化能力', icon: new URL('@/assets/products/PIcon/p5.png', import.meta.url).href, path: '/product/edge-cloud-platform', bgImage: new URL('@/assets/products/bg/edge-cloud.svg', import.meta.url).href, bgPosition: 'right center' },
    { title: '智算云', desc: '通算智算一体，算力切分调度', icon: new URL('@/assets/products/PIcon/p5.png', import.meta.url).href, path: '/product/edge-cloud/cloud-host', bgImage: new URL('@/assets/products/bg/zhi-suan-yun.svg', import.meta.url).href }
  ],
  'compute-network': [
    { title: 'SD-WAN', desc: '跨域互联、传输加速、基于业务构建动态overlay网络', icon: new URL('@/assets/products/PIcon/p6.png', import.meta.url).href, path: '/product/compute-network/interconnect', bgImage: new URL('@/assets/products/bg/crossdomain.svg', import.meta.url).href, bgPosition: 'right center' },
    { title: '多云管理', desc: '统一纳管多云、多数据中心资源，实现集中管控、资源调度、成本管控与运维一体化。', icon: new URL('@/assets/products/PIcon/p6.png', import.meta.url).href, path: '/product/multi-cloud-management', bgImage: new URL('@/assets/products/bg/multi-cloud.svg', import.meta.url).href, bgPosition: 'right center' },
    { title: 'TB级智能流量筛选', desc: '按需自动化筛选流量', icon: new URL('@/assets/products/PIcon/p4.png', import.meta.url).href, path: '/product/compute-network/ecosystem', bgImage: new URL('@/assets/products/bg/filter.svg', import.meta.url).href, bgPosition: 'right center' },
  ],
  'token-gateway': [
    { title: 'AI Token 网关', desc: '统一接入、智能调度、安全可控的多模型 AI 入口', icon: new URL('@/assets/products/PIcon/16.png', import.meta.url).href, path: '/product/ai-token-gateway', bgImage: new URL('@/assets/products/bg/ai-token-gateway.svg', import.meta.url).href, bgPosition: 'right center' }
  ],
  'cloud': [
    { title: 'AI 推理平台', desc: '提供从IaaS、PaaS、MaaS的完整能力', icon: new URL('@/assets/products/PIcon/16.png', import.meta.url).href, path: '/product/ai-computing-platform', bgImage: new URL('@/assets/products/bg/ai-computing.svg', import.meta.url).href, bgPosition: 'right center' },
    { title: 'AI native 平台', desc: '用于生产和治理可审计、可托管的智能体', icon: new URL('@/assets/products/PIcon/15.png', import.meta.url).href, path: '/product/ai-native-platform', bgImage: new URL('@/assets/products/bg/ai-native-platform.svg', import.meta.url).href, bgPosition: 'right center' },
    { title: 'AI 场景智能应用', desc: '把智能体能力交付到具体业务场景', icon: new URL('@/assets/products/PIcon/16.png', import.meta.url).href, path: '/product/ai-agent-application', bgImage: new URL('@/assets/products/bg/ai-scene-application.svg', import.meta.url).href, bgPosition: 'right center' }
  ],
  'cloud-security': [
    { title: '下一代防火墙', desc: '网络运行可靠稳定和业务数据安全无忧', icon: new URL('@/assets/products/PIcon/p8.png', import.meta.url).href, path: '/product/next-gen-firewall', bgImage: new URL('@/assets/products/bg/firewall.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: 'Web应用防火墙', desc: '提供SQL 注入、XSS 跨站等 Web 攻击防护能力，防护网页业务安全', icon: new URL('@/assets/products/PIcon/p9.png', import.meta.url).href, path: '/product/web-application-firewall', bgImage: new URL('@/assets/products/bg/waf.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: '数据库审计综合管理系统', desc: '完全独立于数据库的审计功能', icon: new URL('@/assets/products/PIcon/13.png', import.meta.url).href, path: '/product/database-audit', bgImage: new URL('@/assets/products/bg/database-audit.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: '日志审计系统', desc: '集中采集分析全网各类日志，统一分析溯源、合规留存并预警异常行为', icon: new URL('@/assets/products/PIcon/11.png', import.meta.url).href, path: '/product/log-audit', bgImage: new URL('@/assets/products/bg/log-audit.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: '脆弱性扫描系统与管理系统', desc: '自动探测网络及系统软漏洞，精准发现安全漏洞并提供修复建议', icon: new URL('@/assets/products/PIcon/12.png', import.meta.url).href, path: '/product/vulnerability-scanner', bgImage: new URL('@/assets/products/bg/vulnerability-scanner.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: '运维审计系统', desc: '提升用户IT管理的安全和效益', icon: new URL('@/assets/products/PIcon/10.png', import.meta.url).href, path: '/product/ops-audit', bgImage: new URL('@/assets/products/bg/ops-audit.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: '主机安全管理系统', desc: '新一代主机安全防护系统', icon: new URL('@/assets/products/PIcon/14.png', import.meta.url).href, path: '/product/host-security-management', bgImage: new URL('@/assets/products/bg/host-security-management.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: '全流量', desc: '实时抓取全网原始流量，深度解析溯源，精准发现隐蔽威胁与异常行为。', icon: new URL('@/assets/products/PIcon/p3.png', import.meta.url).href, path: '/product/full-traffic', bgImage: new URL('@/assets/products/bg/full-traffic.jpg', import.meta.url).href, bgPosition: 'right center' },
    { title: '量子密钥管理系统', desc: '统一管控量子密钥全生命周期，实现密钥分发、存储调度与安全协同应用。', icon: new URL('@/assets/products/PIcon/p7.png', import.meta.url).href, path: '/product/quantum-key-management', bgImage: new URL('@/assets/products/bg/quantum-kms.jpg', import.meta.url).href, bgPosition: 'right center' },
  ]
}

const currentProducts = computed(() => products[activeTab.value] || [])
const isTokenGatewayTab = computed(() => activeTab.value === 'token-gateway')
const tokenGatewayBg = products['token-gateway'][0].bgImage

const statsItems = [
  { target: 10, suffix: '万', label: '企业级用户' },
  { target: 1000, suffix: '', label: '落地项目' },
  { target: 200, suffix: '', label: '地市覆盖' },
  { target: 12, suffix: '年', label: '生产环境验证' }
]

const activeTestimonialIndex = ref(0)

const testimonials = [
  {
    quote: [
      '某电视台 2015 年启动智慧媒体建设，瑞和云图依托云安全资源池为其建成国内云安全平台。',
      '随其向融媒体转型，平台历经新闻云、融合媒体一期/二期多轮迭代，覆盖采编、编审、分发全链路，实现边界防护、安全检测、行为审计、威胁处置能力弹性调度，成为广电融媒体领域云安全建设标杆。'
    ],
    author: '某电视台',
    position: '技术负责人',
    tag: '2015年建成云安全平台，覆盖采编发存全链路，持续升级至今',
    tripartite: true,
    diagramImage: new URL('@/assets/anli1-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli1-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli1-light.png', import.meta.url).href
  },
  {
    quote: [
      '某有线运营商是北京地区有线电视网络运营商，其私有云承载广播电视播控、信息服务等关键业务，安全合规要求严苛。',
      '瑞和云图依托云安全资源池为其搭建云安全防护与管理平台，提供虚拟防火墙、入侵检测、WAF、安全隔离、流量管控等一体化能力，助力该私有云在国内率先通过等保 2.0 三级测评。'
    ],
    author: '某有线运营商',
    position: '运维部门负责人',
    tag: '建成通过等保2.0三级评测的私有云，承载广播电视核心业务',
    tripartite: true,
    diagramImage: new URL('@/assets/anli2-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli2-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli2-light.png', import.meta.url).href
  },
  {
    quote: [
      '某国家通讯社拥有全球新闻采集网络与多语种全渠道发布能力，采编、审核、发布业务大量运行于云平台，数据价值高、合规要求严。',
      '瑞和云图基于云安全资源池为其规模化落地云安全平台，构建集安全检测、边界防护、行为审计、威胁处置于一体的纵深防护体系，保障新闻生产、审核、发布全流程安全可靠。'
    ],
    author: '某国家通讯社',
    position: '信息管理部门',
    tag: '建立覆盖全球的融媒体云安全防护平台',
    tripartite: true,
    diagramImage: new URL('@/assets/anli5-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli5-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli5-light.png', import.meta.url).href
  },
  {
    quote: [
      '为支撑冬奥会电力保供，国网某单位依托能源大数据中心承载核心业务。',
      '瑞和云图面向能源关键信息基础设施输出云原生一体化安全防护方案，统筹部署云安全管理、边界防护、Web 应用防护、日志审计、主机安全等全套能力，支撑项目高标准通过等保 2.0 三级测评。'
    ],
    author: '国网某单位',
    position: '能源大数据中心',
    tag: '新能源算力基础设施安全实践',
    tripartite: true,
    diagramImage: new URL('@/assets/anli6-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli6-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli6-light.png', import.meta.url).href
  },
  {
    quote: [
      '电信运营商为支撑全省政企、工业、AI 视频等下沉业务，需建设多点分布的省级边缘云，但原有架构硬件分散、站点标准不一，运维成本高、回传时延大。',
      '瑞和云图依托超融合基础设施打造省级边缘云平台，采用中心云 + 多地市边缘节点的云边协同架构：中心云统一纳管全省站点、统筹算力调度与 AI 模型分发；各地市部署标准化超融合集群，就近提供计算、存储、网络及边缘 AI 推理能力，大幅降低带宽开销。'
    ],
    author: '电信运营商',
    position: '政企边缘云',
    tag: '省级边缘云云边协同实践',
    tripartite: true,
    diagramImage: new URL('@/assets/anli7-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli7-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli7-light.png', import.meta.url).href
  },
  {
    quote: [
      '在东数西算背景下，重庆作为全国八大国家算力枢纽节点，对云平台安全防护与数据抗破译能力要求极高。',
      '瑞和云图以云安全资源池为能力底座，搭建涵盖边界防护、Web 应用防护、审计分析、漏洞检测、主机安全的主动防御云安全平台，并将量子加密能力原生融入资源池，建成国内首个具备量子加密能力的云安全平台，提供云原生 + 量子增强双重保障。'
    ],
    author: '某国家级量子通信运营商',
    position: '算力枢纽安全',
    tag: '具备量子加密能力的云安全平台，为国家级算力枢纽提供双重加密保障',
    tripartite: true,
    diagramImage: new URL('@/assets/anli8-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli8-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli8-light.png', import.meta.url).href
  },
  {
    quote: [
      '某能源局存量部署 ARM、X86 多类型国产芯片服务器，物理单机模式资源浪费突出、缺乏集群冗余与备份，难以满足政务云集约化与信创改造要求。',
      '瑞和云图基于国产超融合底座充分利旧现有服务器，通过软件定义技术完成资源池化重构，搭建自主可控的信创私有云平台，承载油料管理、内部办公、视频监控等业务；依托分布式存储与虚拟网络实现多网段隔离，支持虚拟机备份、故障自动迁移与一键恢复，达成信创替代、资源集约与高可靠保障。'
    ],
    author: '某能源局',
    position: '信创云平台',
    tag: '能源政务信创超融合利旧实践',
    tripartite: true,
    diagramImage: new URL('@/assets/anli9-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli9-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli9-light.png', import.meta.url).href
  },
  {
    quote: [
      '某大型工业互联网是省级 AI 大数据算力核心平台，服务全省智能化改造。针对传统架构算力割裂、硬件规格不统一、时延高、运维难等痛点，',
      '瑞和云图采用云端 + 边缘双层架构建设全域一体化算力网络：云端集中承接 AI 模型训练与全域数据汇总，前端点位部署轻量化算力一体机就近完成智能推理与实时分析，云边之间通过虚拟路由器建立加密隧道保障传输，形成云端训练、边缘推理的 AI 业务闭环。'
    ],
    author: '某大型工业互联网',
    position: '省级算力平台',
    tag: '省级AI算力网络云边协同实践',
    tripartite: true,
    diagramImage: new URL('@/assets/anli10-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli10-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli10-light.png', import.meta.url).href
  },
  {
    quote: [
      '轨道交通场站作业点位分散、场景复杂，人员危险作业与设备异常风险突出；传统监控仅能录像回看，视频全量回传云端带宽开销大、时延高。',
      '瑞和云图依托云边协同超融合算力底座，搭载自研 CV 计算机视觉模型，针对场站目标检测、图像分割、威胁姿态预测开展专项训练。边缘节点就近完成关键帧处理与本地推理，按多级风险评估判定：高风险现场直接报警，中风险输出黄色预警，低置信度可疑样本上传中心云深度分析；云端完成样本标注与模型迭代后下发边缘，形成“边缘实时识别、云端迭代优化”闭环。'
    ],
    author: '某轨道交通',
    position: '场站智能安防',
    tag: '场站边缘CV云边一体智能安防',
    tripartite: true,
    diagramImage: new URL('@/assets/anli11-diagram.png', import.meta.url).href,
    featureImage: new URL('@/assets/anli11-feature.png', import.meta.url).href,
    bgImage: new URL('@/assets/anli11-light.png', import.meta.url).href
  }
]

const currentTestimonial = computed(() => testimonials[activeTestimonialIndex.value])
const testimonialParagraphs = computed(() => {
  const quote = currentTestimonial.value?.quote
  return Array.isArray(quote) ? quote : [quote]
})

const testimonialTimer = ref(null)
const testimonialPaused = ref(false)
const TESTIMONIAL_INTERVAL = 3000

const clearTestimonialTimer = () => {
  if (testimonialTimer.value) {
    clearTimeout(testimonialTimer.value)
    testimonialTimer.value = null
  }
}

const startTestimonialTimer = () => {
  clearTestimonialTimer()
  if (testimonialPaused.value || testimonials.length <= 1) return
  testimonialTimer.value = setTimeout(() => {
    nextTestimonial()
    startTestimonialTimer()
  }, TESTIMONIAL_INTERVAL)
}

const pauseTestimonialAutoplay = () => {
  testimonialPaused.value = true
  clearTestimonialTimer()
}

const resumeTestimonialAutoplay = () => {
  testimonialPaused.value = false
  startTestimonialTimer()
}

const prevTestimonial = () => {
  if (activeTestimonialIndex.value > 0) {
    activeTestimonialIndex.value--
  } else {
    activeTestimonialIndex.value = testimonials.length - 1
  }
  startTestimonialTimer()
}

const nextTestimonial = () => {
  if (activeTestimonialIndex.value < testimonials.length - 1) {
    activeTestimonialIndex.value++
  } else {
    activeTestimonialIndex.value = 0
  }
}

const onNextTestimonialClick = () => {
  nextTestimonial()
  startTestimonialTimer()
}

const goToTestimonial = (index) => {
  activeTestimonialIndex.value = index
  startTestimonialTimer()
}

const statsAnimatedValues = ref([0, 0, 0, 0])
const statsAnimated = ref(false)

const displayStatsValue = (index) => {
  const item = statsItems[index]
  const val = Math.floor(statsAnimatedValues.value[index])
  return val + item.suffix
}

const animateStats = () => {
  if (statsAnimated.value) return
  statsAnimated.value = true
  const duration = 1200
  const startTime = performance.now()

  const step = (currentTime) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const eased = 1 - Math.pow(1 - progress, 4)

    statsItems.forEach((item, i) => {
      const startVal = Math.floor(item.target * 0.05)
      const range = item.target - startVal
      statsAnimatedValues.value[i] = Math.floor(startVal + range * eased)
    })

    if (progress < 1) {
      requestAnimationFrame(step)
    } else {
      statsItems.forEach((item, i) => {
        statsAnimatedValues.value[i] = item.target
      })
    }
  }

  requestAnimationFrame(step)
}

const statsSectionRef = ref(null)

onMounted(() => {
  startTestimonialTimer()

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateStats()
          observer.disconnect()
        }
      })
    },
    { threshold: 0.3 }
  )

  if (statsSectionRef.value) {
    observer.observe(statsSectionRef.value)
  }
})

onBeforeUnmount(() => {
  clearTestimonialTimer()
})
</script>

<style scoped>
.products-section {
  padding: 60px 0 80px;
  background: #fefefe;
  border-bottom: none;
}

.products-section .products-slogan {
  background: none !important;
  -webkit-background-clip: border-box;
  background-clip: border-box;
  -webkit-text-fill-color: var(--color-home-heading);
  color: var(--color-home-heading);
}

.products-section .tab-btn {
  color: rgba(30, 64, 175, 0.4);
}

.products-section .tab-btn:hover {
  color: rgba(30, 64, 175, 0.6);
}

.products-section .tab-btn.active {
  color: var(--color-home-heading);
}

.section-title {
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 48px;
  background: linear-gradient(180deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.products-tabs {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 48px;
}

.tab-btn {
  padding: 0;
  font-size: 20px;
  font-weight: 500;
  color: rgba(30, 41, 59, 0.35);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.3s ease;
}

.tab-btn:hover {
  color: rgba(30, 41, 59, 0.6);
}

.tab-btn.active {
  color: var(--color-home-heading);
  font-weight: 600;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 288px;
  gap: 0;
  max-width: none;
  width: 100%;
  margin: 0 auto;
  padding: 0 12%;
  box-sizing: border-box;
}

.products-grid .product-card {
  border-radius: 0;
  border: none;
  border-right: 1px solid rgba(226, 232, 240, 0.85);
  border-bottom: 1px solid rgba(226, 232, 240, 0.85);
}

.products-grid .product-card:nth-child(3n + 1) {
  border-radius: 12px 0 0 12px;
}

.products-grid .product-card:nth-child(3n) {
  border-radius: 0 12px 12px 0;
  border-right: none;
}

.products-grid--token {
  position: relative;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  grid-auto-rows: auto;
  align-items: stretch;
  min-height: 400px;
}

.token-gateway-stage {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 12%;
  right: 12%;
  z-index: 2;
  pointer-events: none;
}

.token-gateway-bg {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  pointer-events: none;
}

.token-bg-hit {
  position: absolute;
  z-index: 3;
  pointer-events: auto;
  cursor: pointer;
}

.token-bg-hit--start {
  left: calc(609 / 1200 * 100%);
  top: calc(292 / 400 * 100%);
  width: calc(150 / 1200 * 100%);
  height: calc(44 / 400 * 100%);
}

.token-bg-hit--detail {
  left: calc(773 / 1200 * 100%);
  top: calc(292 / 400 * 100%);
  width: calc(110 / 1200 * 100%);
  height: calc(44 / 400 * 100%);
}

.products-grid--token .product-card,
.products-grid--token .product-card:only-child,
.products-grid--token .product-card:nth-child(3n + 1),
.products-grid--token .product-card:nth-child(3n) {
  position: relative;
  z-index: 1;
  background: transparent;
  border: none;
  border-radius: 12px 0 0 12px;
  pointer-events: none;
}

.products-grid--token .product-card:hover .product-bg-image,
.products-grid--token .product-card:hover .product-bg-image::after {
  opacity: 0 !important;
  visibility: hidden;
}

.products-grid--token .product-bg-image {
  opacity: 0 !important;
  visibility: hidden;
}

.products-grid--token .product-content,
.token-gateway-intro h3,
.token-gateway-intro p,
.token-gateway-intro .token-intro-actions {
  opacity: 0;
}

.token-gateway-intro {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  min-height: 400px;
  padding: 36px 32px;
}

/* 最后一行去掉底部分割线（与解决方案区一致） */
.products-grid .product-card:nth-child(3n + 1):nth-last-child(-n + 3),
.products-grid .product-card:nth-child(3n + 2):nth-last-child(-n + 2),
.products-grid .product-card:nth-child(3n):nth-last-child(-n + 1) {
  border-bottom: none;
}

.product-card {
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  padding: 32px 28px;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
}

.product-bg-image {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-size: auto 100%;
  background-position: right center;
  background-repeat: no-repeat;
  opacity: 0.62;
  filter: brightness(1.1) saturate(0.92);
  transition: opacity 0.3s ease;
  z-index: 0;
  pointer-events: none;
}

.product-bg-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.35) 0%,
    rgba(255, 255, 255, 0.12) 24%,
    transparent 48%
  );
  pointer-events: none;
}

.product-card:hover .product-bg-image {
  opacity: 0.72;
}

.product-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 58%;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.product-title {
  margin: 0;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
  font-size: 21px;
  line-height: 1.3;
  letter-spacing: 0.02em;
  text-shadow: none;
  white-space: normal;
  overflow: visible;
  word-break: break-word;
}

.product-title-block {
  display: flex;
  align-items: stretch;
  gap: 10px;
  min-height: 26px;
  flex-shrink: 0;
}

.product-title-accent {
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #60a5fa 0%, #1d4ed8 100%);
  flex-shrink: 0;
}

.product-title-divider {
  width: 100%;
  max-width: 168px;
  height: 1px;
  margin: 10px 0 12px 14px;
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.45) 0%, rgba(59, 130, 246, 0.08) 100%);
  flex-shrink: 0;
}

.product-desc {
  margin: 0 0 14px 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, 'Noto Sans SC', sans-serif;
  font-size: 17px;
  font-weight: 400;
  color: #475569;
  line-height: 1.55;
  letter-spacing: 0.01em;
  text-shadow: none;
  flex: 1;
}

.product-card:hover {
  border-color: transparent;
  box-shadow: none;
  transform: none;
  z-index: 1;
}

.product-card:hover .product-title {
  color: #0f172a;
}

.product-icon {
  font-size: 15px;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 20px;
  transition: text-shadow 0.3s ease;
}

.product-card:hover .product-desc {
  color: #334155;
}

.product-btn {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-left: 14px;
  margin-top: auto;
  flex-shrink: 0;
  padding: 7px 18px;
  border-radius: 999px;
  background: linear-gradient(90deg, #60a5fa 0%, #2563eb 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.32);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.product-card:hover .product-btn {
  color: #ffffff;
  box-shadow: 0 6px 22px rgba(37, 99, 235, 0.45);
  transform: translateY(-1px);
}

.stats-section {
  padding: 100px 0;
  background: #fefefe;
  position: relative;
  scroll-margin-top: 80px;
}

.stats-section .section-title {
  background: none;
  -webkit-background-clip: border-box;
  background-clip: border-box;
  -webkit-text-fill-color: var(--color-home-heading);
  color: var(--color-home-heading);
  font-size: 42px;
  text-align: right;
  letter-spacing: 0.45em;
  padding: 0 calc(12% + 0.45em) 0 12%;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  max-width: none;
  width: 100%;
  margin: 0 auto;
  padding: 8px 12% 0;
  box-sizing: border-box;
  overflow: hidden;
}

.stat-item {
  position: relative;
  padding: 48px 20px;
  min-height: 220px;
  background: #ffffff;
  border: none;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  border-radius: 0;
  box-shadow: none;
  transition: background 0.25s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  box-sizing: border-box;
}

.stats-grid .stat-item:first-child {
  border-left: 1px solid #e5e7eb;
  border-radius: 12px 0 0 12px;
}

.stats-grid .stat-item:last-child {
  border-radius: 0 12px 12px 0;
}

.stat-item::before,
.stat-item::after {
  display: none;
}

.stat-item:hover {
  transform: none;
  background: #dbeafe;
  box-shadow: none;
}

.stat-value {
  font-size: 56px;
  font-weight: 700;
  color: var(--color-home-heading);
  line-height: 1.1;
  margin-bottom: 18px;
  white-space: nowrap;
  max-width: 100%;
}

.stat-plus {
  font-size: 0.72em;
  font-weight: 600;
}

.stat-label {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-home-heading);
  letter-spacing: 0.5px;
  line-height: 1.35;
}

.testimonials-section {
  max-width: none;
  width: 100%;
  margin: 80px auto 0;
  padding: 0 12%;
  box-sizing: border-box;
}

.testimonial-carousel {
  position: relative;
}

.testimonial-card-wrapper {
  position: relative;
}

.testimonial-large-card {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  width: 100%;
  min-height: 480px;
  height: auto;
  box-sizing: border-box;
  background: #e8f2fb;
  border: 1px solid #d4e4f4;
  box-shadow: 0 8px 32px rgba(30, 58, 138, 0.08);
  transition: opacity 0.5s ease;
}

.testimonial-card-content {
  display: flex;
  align-items: stretch;
  min-height: 480px;
  height: auto;
  width: 100%;
  box-sizing: border-box;
}

.testimonial-left {
  flex: 0 0 50%;
  max-width: 50%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  padding: 24px 28px 52px 44px;
  box-sizing: border-box;
}

.testimonial-visual {
  flex: 1 1 50%;
  max-width: 50%;
  min-width: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 24px;
  box-sizing: border-box;
  overflow: hidden;
  background-color: #e8f2fb;
}

.testimonial-visual-art {
  display: block;
  width: auto;
  height: 424px;
  max-width: 100%;
  object-fit: contain;
  object-position: right center;
}

.testimonial-card-content.is-tripartite .testimonial-left {
  flex: 0 0 50%;
  max-width: 50%;
  padding-right: 20px;
}

.testimonial-diagram,
.testimonial-feature {
  min-width: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
  background-color: #e8f2fb;
}

.testimonial-diagram {
  flex: 0 0 26%;
  max-width: 26%;
  padding: 16px 0;
}

.testimonial-feature {
  flex: 1 1 24%;
  max-width: 24%;
  padding: 12px 16px 12px 0;
}

.testimonial-diagram-art,
.testimonial-feature-art {
  display: block;
  width: 100%;
  height: 424px;
  max-height: 100%;
  object-fit: contain;
}

.testimonial-diagram-art {
  object-position: center;
}

.testimonial-feature-art {
  object-position: center right;
}

.testimonial-main-text {
  font-size: 18px;
  line-height: 1.7;
  color: #334155;
  font-weight: 400;
  letter-spacing: 0;
  overflow: visible;
  flex: 0 1 auto;
}

.testimonial-main-text p {
  margin: 0;
  text-indent: 2em;
}

.testimonial-main-text p + p {
  margin-top: 0.9em;
}

.testimonial-footer {
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.testimonial-author-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.author-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-home-heading);
}

.testimonial-tag-line {
  font-size: 18px;
  font-weight: 700;
  color: #64748b;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-home-heading);
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.carousel-arrow:hover {
  background: #fff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.carousel-arrow.prev {
  left: -24px;
}

.carousel-arrow.next {
  right: -24px;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
}

.carousel-dots .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(30, 58, 138, 0.22);
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dots .dot:hover {
  background: rgba(30, 58, 138, 0.5);
}

.carousel-dots .dot.active {
  background: var(--color-home-heading);
  width: 28px;
  border-radius: 5px;
}

@media (max-width: 1023px) {
  .products-section {
    padding: 36px 0 48px;
  }

  .section-title {
    font-size: 24px;
    margin-bottom: 28px;
    padding: 0 20px;
    line-height: 1.35;
  }

  .products-slogan {
    font-size: 22px;
  }

  .products-tabs {
    gap: 10px 14px;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0 16px;
    margin-bottom: 28px;
  }

  .tab-btn {
    font-size: 14px;
    line-height: 1.3;
    padding: 4px 2px;
  }

  .products-grid,
  .solutions-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    gap: 14px;
  }

  .products-grid {
    padding: 0 16px;
  }

  .products-grid--token {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .token-gateway-stage {
    position: relative;
    inset: auto;
    left: 0;
    right: 0;
    min-height: 0;
    height: auto;
    aspect-ratio: 1200 / 400;
    pointer-events: auto;
    background: none;
    border: none;
    box-shadow: none;
  }

  .token-gateway-bg {
    background-size: 100% 100%;
    background-color: transparent;
    border-radius: 16px;
  }

  .products-grid--token .product-card {
    display: none;
  }

  .token-gateway-intro {
    display: none;
  }

  .products-grid .product-card,
  .products-grid .product-card:nth-child(3n + 1),
  .products-grid .product-card:nth-child(3n),
  .products-grid .product-card:last-child {
    border-radius: 12px;
    border: 1px solid rgba(226, 232, 240, 0.8);
  }

  .product-card {
    aspect-ratio: auto;
    min-height: 200px;
    height: auto;
    padding: 24px 20px;
  }

  .product-content {
    max-width: 70%;
  }

  .product-title {
    font-size: 18px;
  }

  .product-desc {
    font-size: 14px;
    margin-left: 14px;
    line-height: 1.5;
  }

  .product-btn {
    font-size: 13px;
    padding: 7px 16px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
    padding: 0 16px;
  }

  .stat-item {
    max-width: none;
    min-width: 0;
    min-height: 140px;
    padding: 28px 16px;
  }

  .stats-section {
    padding: 48px 0;
  }

  .testimonials-section {
    padding: 0 16px;
    margin-top: 40px;
  }

  .section-title,
  .solutions-title {
    font-size: 24px;
  }

  .stat-value {
    font-size: clamp(28px, 8vw, 40px);
  }

  .stat-plus {
    font-size: 0.72em;
  }

  .stat-label {
    font-size: 14px;
  }

  .testimonial-large-card {
    min-height: 0;
    height: auto;
  }

  .testimonial-card-content {
    flex-direction: column;
    min-height: 0;
    padding: 0;
  }

  .testimonial-left {
    flex: 1 1 auto;
    max-width: 100%;
    padding: 24px 20px 14px;
  }

  .testimonial-visual {
    flex: 0 0 180px;
    max-width: 100%;
    height: 180px;
    padding: 8px 14px;
  }

  .testimonial-visual-art {
    height: 100%;
    max-height: 164px;
  }

  .testimonial-card-content.is-tripartite {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .testimonial-card-content.is-tripartite .testimonial-left {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .testimonial-diagram,
  .testimonial-feature {
    flex: 1 1 50%;
    max-width: 50%;
    height: 160px;
    padding: 8px 10px;
  }

  .testimonial-diagram-art,
  .testimonial-feature-art {
    height: 100%;
    max-height: 144px;
  }

  .testimonial-main-text {
    font-size: 16px;
  }

  .author-name {
    font-size: 16px;
  }

  .testimonial-tag-line {
    font-size: 13px;
  }

  .carousel-arrow {
    width: 32px;
    height: 32px;
  }

  .carousel-arrow.prev {
    left: 4px;
  }

  .carousel-arrow.next {
    right: 4px;
  }
}

@media (max-width: 480px) {
  .products-slogan {
    font-size: 20px;
  }

  .products-tabs {
    gap: 8px 12px;
  }

  .tab-btn {
    font-size: 13px;
  }

  .product-content {
    max-width: 100%;
  }

  .product-bg-image {
    opacity: 0.45;
  }
}

</style>
